import React from 'react'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import StatusBadge from '../shared/StatusBadge'

/**
 * "Membership term": the current term and the next one (see membershipTerms).
 * onRenew shows the Renew button on a not-yet-renewed next term.
 */

const row = { display: 'flex', flexWrap: 'wrap', gap: '4px 24px', padding: '14px 0', borderTop: '1px solid #eceff3' }
const label = { minWidth: 120, color: '#5b6573', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, paddingTop: 2 }

const range = (start, end) => (start && end ? `${start} – ${end}` : end ? `Ends ${end}` : start ? `From ${start}, ongoing` : null)

const TermCard = ({ terms, planName, onRenew, className }) => {
    if (!terms || (!terms.current && !terms.next)) return null
    const { current, next } = terms

    return <Paper className={className} data-testid="term-card">
        <Typography component="h2" style={{ fontWeight: 600, fontSize: 18, marginBottom: 4 }}>Membership term</Typography>

        {current && <div style={row} data-testid="term-current">
            <span style={label}>{current.expired ? 'Last term' : 'Current term'}</span>
            <div style={{ flex: 1, minWidth: 220 }}>
                <Typography style={{ fontWeight: 600 }}>
                    {current.expired
                        ? (current.end ? `Ended ${current.end}` : 'Expired')
                        : current.ongoing
                            ? (current.start ? `Member since ${current.start}` : 'Current membership')
                            : range(current.start, current.end) || 'Not scheduled'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {[planName,
                        current.ongoing ? 'Ongoing, billed monthly' : null,
                        current.through ? `Through ${current.through}'s membership` : null,
                        current.daysLeft !== null && current.daysLeft !== undefined ? `${current.daysLeft} days left` : null
                    ].filter(Boolean).join(' · ')}
                </Typography>
            </div>
        </div>}

        {next && <div style={row} data-testid="term-next">
            <span style={label}>Next term</span>
            <div style={{ flex: 1, minWidth: 220 }}>
                {next.state === 'renewed' && <>
                    <Typography style={{ fontWeight: 600 }}>
                        {next.current ? 'Renewed — you are in your new term' : range(next.start, next.end)}{' '}
                        <StatusBadge label="Renewed" />
                    </Typography>
                    <Typography variant="body2" color="textSecondary">{[planName, next.rate].filter(Boolean).join(' · ')}</Typography>
                </>}
                {next.state === 'in_progress' && <>
                    <Typography style={{ fontWeight: 600 }}>{range(next.start, next.end) || 'Your renewal'} <StatusBadge label="In progress" /></Typography>
                    <Typography variant="body2" color="textSecondary">Complete your renewal to continue into the next term.</Typography>
                </>}
                {next.state === 'with_primary' && <Typography variant="body2" color="textSecondary">
                    Your next term starts when {next.primaryName || (current && current.through) || 'your primary member'} renews; you'll get an email then.
                </Typography>}
                {next.state === 'not_renewed' && <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <Typography style={{ fontWeight: 600 }}>Not renewed yet</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Renew by {next.renewBy} to keep your membership{next.rate ? ` (${next.rate})` : ''}.
                        </Typography>
                    </div>
                    {onRenew && <Button variant="outlined" color="primary" size="small" onClick={onRenew}>Renew membership</Button>}
                </div>}
            </div>
        </div>}
    </Paper>
}

export default TermCard
